import thunk from 'redux-thunk';
import axios from 'axios';
import * as types from './actionTypes';
import { handleError } from '../errors/handleError';
import { checkProjectConfig } from './projectConfigActions';
import { getProjectVersion } from './projectVersionsActions';

// ERROR HANDLER
export function errorHandler(dispatch, error, type) {
    const errorMessage = handleError(error);

    dispatch({
        type,
        error: errorMessage,
    });
}

// triggers clearing size warning
export function resetWarning() {
    return {
        type: types.RESET_WARNING,
        payload: 'Image size warning has been reset',
    };
}

// triggers clearing cropped image data from redux store
export function resetCroppedImage() {
    return {
        type: types.RESET_CROPPED_IMAGE,
    };
}

// triggers clearing all image data from redux store
export function resetImageData() {
    return {
        type: types.RESET_IMAGE_DATA,
    };
}

// Handles close of cropper without save
export function clearCropModal() {
    return {
        type: types.CLOSE_CROPPER,
    };
}

// Uploads the original and dispatches the data for cropper to use, also uploads and saves the cropped version
export function uploadAssetImageForCrop(projectId, imageType, file, parentId = '00000000-0000-0000-0000-000000000000', crop = false) {
    const imageFormData = new FormData();
    imageFormData.append('imageFile', file, `logo${Math.random()
        .toString(36)
        .replace(/[^a-z]+/g, '')
        .substr(0, 5)}.png`);
    return function (dispatch) {
        dispatch({
            type: types.UPLOAD_ASSET_IMAGE_CROP_PENDING,
        });
        return axios({
            url: `${window.GENESIS_CMS_API_URL}/v2/projects/${projectId}/uploadlatest?sessionid=${localStorage.getItem('SessionId')}&token=${localStorage.getItem('AuthToken')}&imageType=${imageType}&crop=${crop}&parentId=${parentId}`,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            data: imageFormData,
            method: 'POST',
        })
            .then((response) => {
                if (response.status === 200) {
                    dispatch({
                        type: types.UPLOAD_ASSET_IMAGE_CROP_SUCCESS,
                        payload: response.data.data,
                        crop,
                    });
                    if (!crop) {
                        dispatch({
                            type: types.CREATE_IMAGE_CROP_SUCCESS,
                            payload: response.data.data,
                        });
                    } else {
                        dispatch({
                            type: types.CROP_IMAGE_CROP_SUCCESS,
                            payload: response.data.data,
                        });
                        dispatch(getProjectVersion(projectId));
                    }
                } else {
                    errorHandler(
                        dispatch,
                        response.data.originalImage.ErrorCode,
                        types.CREATE_IMAGE_CROP_FAILED,
                    );
                }
            })
            .catch((error) => {
                errorHandler(dispatch, error.response, types.UPLOAD_ASSET_IMAGE_CROP_FAILED);
            });
    };
}

// Action called by the cropper itself
export function cropImageForCrop(projectId, imageType, file, parentId) {
    return function (dispatch) {
        dispatch({
            type: types.CROP_IMAGE_CROP_PENDING,
        });
        return dispatch(uploadAssetImageForCrop(projectId, imageType, file, parentId, true));
    };
}

// Use for live previews, displays of image, etc - called for logo on pages that use logo in live preview
export function getOriginalCroppedImageForCrop(projectId, imageType, parentId) {
    return function (dispatch) {
        dispatch({
            type: types.GET_CROPPED_IMAGE_CROP_PENDING,
        });

        return axios.get(`${window.GENESIS_CMS_API_URL}/v2/projects/${projectId}/images/croppedImageLatest?imageType=${imageType}&sessionId=${localStorage.getItem('SessionId')}&token=${localStorage.getItem('AuthToken')}&parentId=${parentId}`)
            .then((response) => {
                if (response.data.data.imagePath == null) {
                    dispatch({
                        type: types.GET_CROPPED_IMAGE_CROP_FAILED,
                    });
                } else {
                    dispatch({
                        type: types.GET_CROPPED_IMAGE_CROP_SUCCESS,
                        payload: response.data.data,
                    });
                }
            })
            .catch((error) => {
                errorHandler(dispatch, error.response, types.GET_CROPPED_IMAGE_CROP_FAILED);
            });
    };
}

// Use to get original image for a recrop
export function getOriginalImageForCrop(projectId, imageType, parentId) {
    return function (dispatch) {
        dispatch({
            type: types.GET_ORIGINAL_IMAGE_CROP_PENDING,
        });

        return axios.get(`${window.GENESIS_CMS_API_URL}/v2/projects/${projectId}/images/originalImageLatest?imageType=${imageType}&sessionId=${localStorage.getItem('SessionId')}&token=${localStorage.getItem('AuthToken')}&parentId=${parentId}`)
            .then((response) => {
                dispatch({
                    type: types.GET_ORIGINAL_IMAGE_CROP_SUCCESS,
                    payload: response.data.data,
                });
                dispatch(resetWarning());
            })
            .catch((error) => {
                errorHandler(dispatch, error.response, types.GET_ORIGINAL_IMAGE_CROP_FAILED);
            });
    };
}

/*
Delete button on logo page. Fully delete original and all crops; for latest version.
 */
export function deleteImagesForCropForCrop(projectId, imageType, parentId) {
    return function (dispatch) {
        dispatch({
            type: types.DELETE_ALL_CROPPED_IMAGES_CROP_PENDING,
        });

        return axios({
            url: `${window.GENESIS_CMS_API_URL}/v2/projects/${projectId}/images/${imageType}/imageslatest?sessionId=${localStorage.getItem('SessionId')}&token=${localStorage.getItem('AuthToken')}&parentId=${parentId}`,
            method: 'DELETE',
        })
            .then((response) => {
                return Promise.all([
                    dispatch({
                        type: types.DELETE_ALL_CROPPED_IMAGES_CROP_SUCCESS,
                        payload: response.data,
                    }),
                    dispatch(getProjectVersion(projectId)),
                    dispatch(checkProjectConfig(projectId)),
                ]);
            })
            .catch((error) => {
                errorHandler(dispatch, error.response, types.DELETE_ALL_CROPPED_IMAGES_CROP_FAILED);
            });
    };
}

/*
Delete images that are uploaded but not cropped.
Can be refactored out when we stop uploading images that aren't saved.
*/
export function deleteSingleImageForCrop(imageId) {
    return function (dispatch) {
        dispatch({
            type: types.DELETE_SINGLE_IMAGE_CROP_PENDING,
        });

        return axios({
            url: `${window.GENESIS_CMS_API_URL}/v2/projects/${imageId}/images?sessionId=${localStorage.getItem('SessionId')}&token=${localStorage.getItem('AuthToken')}`,
            method: 'DELETE',
        })
            .then((response) => {
                dispatch({
                    type: types.DELETE_SINGLE_IMAGE_CROP_SUCCESS,
                    payload: response.data,
                });
            })
            .catch((error) => {
                errorHandler(dispatch, error.response, types.DELETE_SINGLE_IMAGE_CROP_FAILED);
            });
    };
}
